// =============================================
// useGenerationQueue — hook for generation_queue CRUD + realtime
// =============================================

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import type { GenerationQueueItem, QueueStatus } from "@/types/thematicMaps";

export function useGenerationQueue(mapId: string) {
  const [items, setItems] = useState<GenerationQueueItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const { data } = await (supabase as any)
      .from("generation_queue")
      .select("*")
      .eq("map_id", mapId)
      .order("priority", { ascending: false });

    setItems((data || []) as GenerationQueueItem[]);
    setLoading(false);
  }, [mapId]);

  // Initial fetch
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Realtime subscription (debounced refetch 400ms)
  const { isLive } = useRealtimeSubscription({
    channel: `genq_${mapId || 'none'}`,
    table: "generation_queue",
    filter: mapId ? `map_id=eq.${mapId}` : undefined,
    enabled: Boolean(mapId),
    onChange: fetchItems,
  });

  const addToQueue = useCallback(
    async (
      nodeId: string,
      step: string,
      priority = 50,
      config: Record<string, unknown> = {},
    ) => {
      // Queue DB-side — procesor (browser lub worker) claimnie gdy bedzie aktywny.
      const { data: session } = await supabase.auth.getSession();
      await (supabase as any).from("generation_queue").insert({
        user_id: session?.session?.user?.id,
        map_id: mapId,
        node_id: nodeId,
        step,
        status: "queued",
        priority,
        config,
      });
    },
    [mapId],
  );

  const updateStatus = useCallback(
    async (queueId: string, status: QueueStatus, extra?: Record<string, unknown>) => {
      await (supabase as any)
        .from("generation_queue")
        .update({ status, updated_at: new Date().toISOString(), ...extra })
        .eq("id", queueId);
    },
    [],
  );

  const cancelItem = useCallback(
    async (queueId: string) => {
      await updateStatus(queueId, "cancelled");
    },
    [updateStatus],
  );

  const clearCompleted = useCallback(async () => {
    await (supabase as any)
      .from("generation_queue")
      .delete()
      .eq("map_id", mapId)
      .in("status", ["completed", "cancelled"]);
  }, [mapId]);

  const queuedCount = items.filter((i) => i.status === "queued").length;
  const inProgressCount = items.filter((i) => i.status === "in_progress").length;
  const completedCount = items.filter((i) => i.status === "completed").length;
  const failedCount = items.filter((i) => i.status === "failed").length;

  return {
    items,
    loading,
    isLive,
    queuedCount,
    inProgressCount,
    completedCount,
    failedCount,
    addToQueue,
    updateStatus,
    cancelItem,
    clearCompleted,
    refresh: fetchItems,
  };
}
